import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "April 2025";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-black">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-black/70 leading-relaxed text-base">
      {children}
    </div>
  </section>
);

const TermsPage = () => {
  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      <section className="relative pt-28 md:pt-36 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <h1 className="mt-6 text-4xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
            Terms of Service
          </h1>
          <p className="mt-3 text-white/55 text-sm">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="bg-white text-black py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-black/70 leading-relaxed">
            Welcome to Good Fruit. By using our website, app, or services
            (collectively, the “Service”), you agree to these Terms. If you
            do not agree, please do not use the Service. Good Fruit is
            operated by Fathertime Technologies.
          </p>

          <Section title="1. The service">
            <p>
              Good Fruit provides a faith-based community platform, biblical
              finance education, premium credit content, and pathways to
              funding through our partner LMI Marketing & Business Solutions
              Group, Inc. We may add, modify, or remove features as the Service
              grows.
            </p>
          </Section>

          <Section title="2. Accounts">
            <p>
              You are responsible for keeping your account credentials secure
              and for all activity under your account. You agree to provide
              accurate information and to update it when it changes.
            </p>
          </Section>

          <Section title="3. Subscriptions, billing, and cancellation">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Premium subscriptions (e.g., Credit Mastery at $29.95/month)
                are billed monthly through Stripe.
              </li>
              <li>
                Subscriptions automatically renew at the same price unless you
                cancel. You can cancel anytime through the link in any Stripe
                receipt; cancellation stops the next renewal.
              </li>
              <li>
                Founders’ Circle members receive 12 months of complimentary
                access subject to the terms communicated at signup.
              </li>
              <li>
                We do not offer refunds for partial billing periods. If you
                cancel, you’ll keep access until the current period ends.
              </li>
            </ul>
          </Section>

          <Section title="4. Educational content disclaimer">
            <p>
              The Biblical Finance Course, Credit Mastery, and any other
              educational content on Good Fruit are provided for{" "}
              <strong>educational and inspirational purposes only</strong>.
              Nothing on the Service is intended as personalized financial,
              legal, tax, or medical advice. Please consult a qualified
              professional before making decisions that affect your finances,
              business, or wellbeing.
            </p>
          </Section>

          <Section title="5. Funding through LMI">
            <p>
              Good Fruit does not directly provide funding. Funding
              applications are routed to our partner{" "}
              <strong>
                LMI Marketing & Business Solutions Group, Inc.
              </strong>
              , which independently evaluates and approves applications under
              its own terms. Approval, terms, and timelines are determined by
              LMI, not Good Fruit.
            </p>
          </Section>

          <Section title="6. Affiliate links and partner tools">
            <p>
              Credit Mastery includes referral links to IdentityIQ and
              CreditScoreIQ. Good Fruit may earn a referral fee if you sign
              up through these links. Their products and services are
              provided directly by those companies under their own terms and
              policies.
            </p>
          </Section>

          <Section title="7. Acceptable use">
            <p>You agree not to use Good Fruit to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Break the law or anyone’s rights, including intellectual property</li>
              <li>Harass, defame, or harm other members</li>
              <li>Misrepresent your identity or affiliation</li>
              <li>Interfere with the Service’s security or normal operation</li>
              <li>Scrape, mass-export, or resell our content without permission</li>
            </ul>
          </Section>

          <Section title="8. AI assistant">
            <p>
              Melanie is an AI assistant powered by Anthropic Claude. Her
              answers are generated by software and may be incorrect. Treat
              her responses as helpful guidance, not personal advice. If
              you’re facing a spiritual, mental health, or financial crisis,
              please reach out to a qualified human professional.
            </p>
          </Section>

          <Section title="9. Intellectual property">
            <p>
              All content on Good Fruit — including text, course materials,
              graphics, logos, and the Good Fruit and T.H.3RD marks — is
              owned by Fathertime Technologies or its licensors and is
              protected by copyright and trademark law. You may not copy,
              distribute, or create derivative works without permission.
            </p>
          </Section>

          <Section title="10. Disclaimer of warranties">
            <p>
              The Service is provided “as is” and “as available.” We do our
              best, but we make no warranties — express or implied — that
              the Service will be uninterrupted, error-free, or fit for a
              particular purpose.
            </p>
          </Section>

          <Section title="11. Limitation of liability">
            <p>
              To the fullest extent permitted by law, Good Fruit and
              Fathertime Technologies are not liable for any indirect,
              incidental, special, or consequential damages arising from your
              use of the Service. Our total liability for any claim is
              limited to the amount you paid us in the 12 months prior to the
              claim.
            </p>
          </Section>

          <Section title="12. Termination">
            <p>
              We may suspend or terminate your access if you violate these
              Terms or use the Service in a way that puts others at risk. You
              may stop using the Service at any time.
            </p>
          </Section>

          <Section title="13. Changes to these terms">
            <p>
              We may update these Terms as Good Fruit grows. Material changes
              will be communicated by email or in-app notice; continued use
              after changes constitutes acceptance.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              Questions about these Terms? Email us — we read every message
              personally.
            </p>
            <p>
              <strong>Good Fruit App / Fathertime Technologies</strong>
              <br />
              Founder: Min. Tyree A. Huddleston III
            </p>
          </Section>

          <div className="mt-12 rounded-xl bg-[#f6faf9] border border-black/10 p-5 text-sm text-black/55 leading-relaxed">
            <strong className="text-black">Note:</strong> These Terms are
            provided as a starting template. Before commercial launch, please
            have them reviewed by a qualified attorney to ensure they meet
            your jurisdiction’s requirements.
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default TermsPage;
